// What a tapped address or phone link in an email body is (2026-10-19, Brian: tapping either brings
// up a small menu — Call / Text, or New message / Forward an email to it). Street addresses joined the
// menu on 2026-10-30: linkify mints `bmail-map:` hrefs for them; tapping offers Apple Maps, Google
// Maps or Copy (Brian's ask: "clicking on an address should ask to open in Apple Maps, Google Maps,
// or copy to clipboard").
//
// 🚩 The href comes from untrusted mail as often as from linkify.ts, so nothing here is passed on
// raw: an address must look like one address, and a phone is reduced to digits and one leading +.

export type ContactLink =
  | { kind: 'email'; address: string }
  | { kind: 'phone'; digits: string; display: string }
  | { kind: 'postal'; address: string };

const EMAIL_ONE = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

/** Digits with at most one leading +, or null outside E.164's 7–15 digits. */
export function phoneDigits(raw: string): string | null {
  const plus = raw.trim().startsWith('+');
  const digits = raw.replace(/\D/g, '');
  if (digits.length < 7 || digits.length > 15) return null;
  return (plus ? '+' : '') + digits;
}

/**
 * The contact a link points at, or null for any other link (those keep opening normally).
 * `text` is the link's visible text, shown in the menu for a phone.
 */
export function contactFromHref(href: string | null | undefined, text = ''): ContactLink | null {
  if (!href) return null;
  // linkify-minted street address: the menu offers maps/copy. bmail-map: is OUR scheme — only
  // linkify mints it — so the decoded text can be taken as the address (still one-lined + length-
  // capped, because the href string itself comes back from the DOM untrusted).
  const map = /^bmail-map:(.+)$/is.exec(href.trim());
  if (map) {
    let target: string;
    try { target = decodeURIComponent(map[1]).replace(/\s+/g, ' ').trim(); } catch { return null; }
    if (!target || target.length > 200) return null;
    return { kind: 'postal', address: target };
  }
  const m = /^(mailto|tel|sms|callto):([^?#]*)/i.exec(href.trim());
  if (!m) return null;
  let target: string;
  try { target = decodeURIComponent(m[2]).trim(); } catch { return null; }
  // RFC 3966 parameters (";ext=89", ";phone-context=…") are not part of the number: their digits
  // would otherwise be dialled on the end of it.
  if (m[1].toLowerCase() !== 'mailto') target = target.split(';')[0];
  if (m[1].toLowerCase() === 'mailto') {
    // One address only: "a@x.com,b@y.com" or a display-name form isn't offered the menu.
    return EMAIL_ONE.test(target) ? { kind: 'email', address: target } : null;
  }
  const digits = phoneDigits(target);
  if (!digits) return null;
  const shown = text.trim();
  return { kind: 'phone', digits, display: shown && phoneDigits(shown) === digits ? shown : digits };
}

/**
 * The compose URL for "New message to …", sent from `from` (the mailbox the email was read in),
 * or forwarding a message or conversation, which is sent from the original's mailbox.
 */
export function composeToUrl(address: string, opts: { from?: string; forward?: { ref: string; thread?: boolean } } = {}): string {
  const to = `to=${encodeURIComponent(address)}`;
  const { forward, from } = opts;
  if (!forward) return `/compose?${to}${from ? `&from=${encodeURIComponent(from)}` : ''}`;
  return `/compose?forward=${encodeURIComponent(forward.ref)}&${to}${forward.thread ? '&thread=1' : ''}`;
}
